import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Input } from '@/shared/components/ui/input'
import { Typography } from '@/shared/components/ui/typography'
import CheckOutlined from '@mui/icons-material/CheckOutlined'
import KeyboardArrowDownOutlined from '@mui/icons-material/KeyboardArrowDownOutlined'
import LanguageOutlined from '@mui/icons-material/LanguageOutlined'
import LinkOutlined from '@mui/icons-material/LinkOutlined'
import LockOutlined from '@mui/icons-material/LockOutlined'
import React, { useState } from 'react'
import UserAvatar from '../../shared/components/UserAvatar'

type Permission = 'owner' | 'can edit' | 'can view'
type AccessLevel = 'Only invited people' | 'Anyone with the link'

type ShareUser = {
  id: string
  name: string
  email: string
  avatar?: string
  isExternal?: boolean
  permission: Permission
  isInviteSent?: boolean
  isWorkspace?: boolean
  memberCount?: number
}

type ShareMenuProps = {
  children: React.ReactNode
  users?: ShareUser[]
  onInvite?: (emails: string[]) => void
  onCopyLink?: () => void
  onPermissionChange?: (userId: string, permission: Permission) => void
  onAccessLevelChange?: (level: AccessLevel) => void
  onRemoveUser?: (userId: string) => void
}

export const ShareMenu: React.FC<ShareMenuProps> = ({
  children,
  users = [],
  onInvite,
  onCopyLink,
  onPermissionChange,
  onAccessLevelChange,
  onRemoveUser,
}) => {
  const [emailInput, setEmailInput] = useState('')
  const [accessLevel, setAccessLevel] = useState<AccessLevel>(
    'Only invited people'
  )

  const handleInvite = () => {
    if (emailInput.trim() && onInvite) {
      const emails = emailInput
        .split(',')
        .map((email) => email.trim())
        .filter(Boolean)
      onInvite(emails)
      setEmailInput('')
    }
  }

  const handleCopyLink = () => {
    if (onCopyLink) {
      onCopyLink()
    }
  }

  const handleAccessLevelChange = (level: AccessLevel) => {
    setAccessLevel(level)
    if (onAccessLevelChange) {
      onAccessLevelChange(level)
    }
  }

  const handlePermissionChange = (userId: string, permission: Permission) => {
    if (onPermissionChange) {
      onPermissionChange(userId, permission)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent className="w-[476px] p-4" align="end" sideOffset={8}>
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <Typography variant="h4" className="font-sans text-lg font-medium">
              Share this file
            </Typography>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyLink}
              className="flex h-8 items-center gap-2 rounded-3xl px-2 text-sm"
            >
              <LinkOutlined sx={{ fontSize: 12 }} />
              <Typography variant="body1" className="text-sm">
                Copy link
              </Typography>
            </Button>
          </div>

          {/* Email Input Section */}
          <div className="flex gap-2">
            <Input
              placeholder="Email or workspace, separated by commas"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="h-8 flex-1 font-sans text-xs"
            />
            <Button
              onClick={handleInvite}
              disabled={!emailInput.trim()}
              size="sm"
              className="h-8"
            >
              Invite
            </Button>
          </div>

          {/* Access Level Section */}
          <div className="relative space-y-2">
            <Typography
              variant="h4"
              className="font-sans text-base font-medium"
            >
              Who has access
            </Typography>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="h-8 w-full justify-between rounded-3xl border-none text-xs"
                >
                  <div className="flex items-center gap-2">
                    {accessLevel === 'Anyone with the link' ? (
                      <LanguageOutlined sx={{ fontSize: 12 }} />
                    ) : (
                      <LockOutlined sx={{ fontSize: 12 }} />
                    )}
                    <span>{accessLevel}</span>
                  </div>
                  <KeyboardArrowDownOutlined sx={{ fontSize: 12 }} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-full min-w-[280px]">
                <DropdownMenuItem
                  onClick={() =>
                    handleAccessLevelChange('Anyone with the link')
                  }
                  className="flex items-start gap-3 p-3"
                >
                  <LanguageOutlined
                    sx={{ fontSize: 16 }}
                    className="mt-0.5 text-gray-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <Typography
                        variant="body1"
                        className="text-sm font-medium"
                      >
                        Anyone with the link
                      </Typography>
                      {accessLevel === 'Anyone with the link' && (
                        <CheckOutlined
                          sx={{ fontSize: 16 }}
                          className="text-blue-600"
                        />
                      )}
                    </div>
                    <Typography
                      variant="body1"
                      className="mt-1 text-xs text-gray-600"
                    >
                      Anyone can access the file using this link
                    </Typography>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleAccessLevelChange('Only invited people')}
                  className="flex items-start gap-3 p-3"
                >
                  <LockOutlined
                    sx={{ fontSize: 16 }}
                    className="mt-0.5 text-gray-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <Typography
                        variant="body1"
                        className="text-sm font-medium"
                      >
                        Only invited people
                      </Typography>
                      {accessLevel === 'Only invited people' && (
                        <CheckOutlined
                          sx={{ fontSize: 16 }}
                          className="text-blue-600"
                        />
                      )}
                    </div>
                    <Typography
                      variant="body1"
                      className="mt-1 text-xs text-gray-600"
                    >
                      Only people who have access to this file can use the link
                    </Typography>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Users List Section */}
          <div className="max-h-48 space-y-2 overflow-y-auto">
            {users.map((user) => (
              <div
                key={user.id}
                className="flex items-center justify-between py-1"
              >
                <div className="flex items-center gap-2">
                  {user.isWorkspace ? (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-100">
                      <Typography
                        variant="body1"
                        className="font-semibold text-purple-600"
                      >
                        M{/* TODO: Add workspace name and icon */}
                      </Typography>
                    </div>
                  ) : (
                    <UserAvatar
                      email={user?.email || ''}
                      initials={user.name.charAt(0)}
                      className="h-10 w-10 text-xs"
                    />
                  )}
                  <div className="flex min-w-0 flex-col">
                    <Typography
                      variant="body1"
                      className="truncate font-medium"
                    >
                      {user.name}
                      {user.isExternal && ' (External user)'}
                      {user.isInviteSent && ' (Invite sent)'}
                    </Typography>
                    {user.isWorkspace && (
                      <Typography variant="body1" className="text-gray-500">
                        {user.memberCount} members
                      </Typography>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 pr-1">
                  {user.permission === 'owner' ? (
                    <Typography
                      variant="body1"
                      className="text-sm text-gray-500"
                    >
                      owner
                    </Typography>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-sm"
                        >
                          {user.permission}
                          <KeyboardArrowDownOutlined
                            sx={{ fontSize: 8 }}
                            className="ml-1"
                          />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-32">
                        <DropdownMenuItem
                          onClick={() =>
                            handlePermissionChange(user.id, 'owner')
                          }
                          className="text-sm"
                        >
                          owner
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handlePermissionChange(user.id, 'can view')
                          }
                          className="text-sm"
                        >
                          can view
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handlePermissionChange(user.id, 'can edit')
                          }
                          className="text-sm"
                        >
                          can edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            if (onRemoveUser) {
                              onRemoveUser(user.id)
                            }
                          }}
                          className="text-sm"
                        >
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
